import { getSsrCatalogue, SsrCatalogue } from "../../services/api";
import React, { useState, useEffect } from "react";
import { FaClipboardList } from "react-icons/fa";
import Checkbox from "../ui/Checkbox";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";
import countryDialInfo from "../../assets/country_dial_info.json";
import {
  validateAgeForPassengerType,
  validatePassportExpiry,
} from "../../utils/dateUtils";
import { frequentFlyerEnabled } from "../../utils/features";

export interface PassengerData {
  title: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phone: string;
  email: string;
  ffNumber?: string;
  /**
   * The airline the number belongs to. Required alongside it: the host entry
   * is FF<carrier><number>, and Sabre validates the pair against that
   * carrier's records — a number without an airline cannot be recorded.
   */
  ffAirline?: string;
  passportNumber: string;
  dob: string;
  expiryDate: string;
  issuingCountry: string;
  agreed: boolean;
  insured: boolean;
  passengerType: "ADT" | "CHD" | "INF";
  /**
   * What the passenger is asking the airline for. Each becomes an SSR on the
   * PNR — a request, not a booking: the airline can and does decline them, and
   * they cost nothing either way.
   */
  /**
   * Ticked when the passenger refuses to give contact details. Sends SSR CTCR,
   * which is the airline's third acceptable answer alongside a mobile and an
   * email — and the only one that closes their warning when there is neither.
   */
  contactDeclined?: boolean;
  mealPreference?: string;
  assistance?: string;
  seatPreference?: "window" | "aisle" | "";
}

interface PassengerFormProps {
  index: number;
  type: "ADT" | "CHD" | "INF";
  value: PassengerData;
  onChange: (data: PassengerData) => void;
  disabled?: boolean;
  departureDate?: string;
  /** Show the priced extra-baggage picker (booking flow only, not Profile). */
  /** Fare currency for baggage prices. */
  currency?: string;
}

const initialErrors = {
  title: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  passportNumber: "",
  dob: "",
  countryCode: "",
  expiryDate: "",
  issuingCountry: "",
  agreed: "",
};

const passportRegex = /^[A-Za-z0-9]{5,15}$/;

/**
 * What the airline will accept as a name — the same rule the server enforces
 * in common/utils/passengerName.ts. Kept in step with it deliberately: this
 * copy saves the agent a round trip, the server's copy is what actually holds.
 *
 * Letters, spaces, hyphens and apostrophes only, because the name is also sent
 * in the passport data block, and a surname of at least 2 characters because
 * Sabre refuses less — which is what turned a paid AED 525 sale into a booking
 * with no ticket.
 */
const nameAllowedRegex = /^[A-Za-z][A-Za-z '-]*$/;
const SURNAME_MIN = 2;
const NAME_MAX = 30;

const nameError = (label: string, raw: string | undefined, min: number): string | undefined => {
  const value = (raw ?? "").trim();
  if (!value) return `${label} is required.`;
  if (value.length < min) return `${label} needs at least ${min} letters, as in the passport.`;
  if (value.length > NAME_MAX) return `${label} must be ${NAME_MAX} characters or fewer.`;
  if (!nameAllowedRegex.test(value)) {
    return `${label} can only contain letters, spaces, hyphens and apostrophes — spell it as in the passport.`;
  }
  return undefined;
};
const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/;
const phoneRegex = /^[0-9]{7,}$/;

const PassengerForm: React.FC<PassengerFormProps> = ({
  index,
  type,
  value,
  disabled = false,
  departureDate,
  onChange,
  currency = "AED",
}) => {
  const [errors, setErrors] = useState(initialErrors);

  // The catalogue is static, so one fetch per mounted form is plenty. A failure
  // is not worth surfacing: special requests are optional, and a passenger who
  // cannot pick a meal can still fly. The section simply does not render.
  const [ssr, setSsr] = useState<SsrCatalogue>({});
  useEffect(() => {
    let live = true;
    getSsrCatalogue()
      .then((c) => { if (live) setSsr(c); })
      .catch(() => { /* optional field; stay silent */ });
    return () => { live = false; };
  }, []);
  const validate = (field?: string, val?: any) => {
    let newErrors = { ...errors };

    const get = (k: keyof PassengerData) =>
      field === k && val !== undefined ? val : value[k];

    if (type !== "INF" && (!get("title") || get("title") === ""))
      newErrors.title = "Title is required.";
    else newErrors.title = "";

    const firstNameError = nameError("First name", get("firstName"), 1);
    if (firstNameError) newErrors.firstName = firstNameError;
    else newErrors.firstName = "";

    const lastNameError = nameError("Last name", get("lastName"), SURNAME_MIN);
    if (lastNameError) newErrors.lastName = lastNameError;
    else newErrors.lastName = "";

    if (!get("phone")) newErrors.phone = "Phone is required.";
    else if (!phoneRegex.test(get("phone")!))
      newErrors.phone = "Enter a valid phone number.";
    else newErrors.phone = "";

    if (!get("email")) newErrors.email = "Email is required.";
    else if (!emailRegex.test(get("email")!))
      newErrors.email = "Enter a valid email.";
    else newErrors.email = "";

    if (type !== "INF") {
      if (!get("passportNumber"))
        newErrors.passportNumber = "Passport number is required.";
      else if (!passportRegex.test(get("passportNumber")!))
        newErrors.passportNumber = "Invalid passport number.";
      else newErrors.passportNumber = "";
    } else {
      newErrors.passportNumber = "";
    }

    if (type !== "INF") {
      if (!get("dob")) {
        newErrors.dob = "Date of birth is required.";
      } else {
        const ageValidation = validateAgeForPassengerType(get("dob")!, type);
        if (!ageValidation.isValid) {
          newErrors.dob = ageValidation.message;
        } else {
          newErrors.dob = "";
        }
      }
    } else {
      newErrors.dob = "";
    }

    if (type !== "INF") {
      if (!get("expiryDate")) {
        newErrors.expiryDate = "Expiry date is required.";
      } else {
        const expiryValidation = validatePassportExpiry(
          get("expiryDate")!,
          departureDate
        );
        if (!expiryValidation.isValid) {
          newErrors.expiryDate = expiryValidation.message;
        } else {
          newErrors.expiryDate = "";
        }
      }
    } else {
      newErrors.expiryDate = "";
    }

    if (type !== "INF" && !get("issuingCountry"))
      newErrors.issuingCountry = "Country of issue is required.";
    else newErrors.issuingCountry = "";

    if (!get("agreed")) newErrors.agreed = "You must agree to terms.";
    else newErrors.agreed = "";

    setErrors(newErrors);

    return Object.values(newErrors).every((e) => !e);
  };

  const handleChange = (field: keyof PassengerData, val: any) => {
    onChange({ ...value, [field]: val, passengerType: type });
    validate(field, val);
  };

  const handleBlur = () => {
    validate();
  };

  const selectedCountryCode = value.countryCode || "+971";

  useEffect(() => {
    if (value.dob || value.expiryDate) {
      validate();
    }
  }, [type, value.dob, value.expiryDate, departureDate]);

  return (
    <div className="bg-[#F6F8FC] border border-[#E4E4E4] p-4 sm:p-5 rounded-2xl shadow-sm space-y-4 sm:space-y-6 relative">
      <div className="flex items-center justify-between text-blue-600">
        <h2 className="text-sm sm:text-subheading font-sora border-b pb-1 text-black">
          Passenger Details
        </h2>
        <FaClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 sm:gap-4">
        <div className="sm:col-span-1">
          <select
            className="mt-1 block w-full p-2 rounded-md border border-gray-300 shadow-lg text-xs sm:text-sm"
            value={value.title}
            onChange={(e) => handleChange("title", e.target.value)}
            onBlur={handleBlur}
            disabled={disabled || type === "INF"}
          >
            <option value="">Select</option>
            <option>Mr</option>
            <option>Mrs</option>
            <option>Ms</option>
            <option>Master</option>
            <option>Miss</option>
          </select>
          {errors.title && (
            <div className="text-2xs sm:text-xs text-red-600">
              {errors.title}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <input
            type="text"
            placeholder="First Name"
            className="mt-1 block p-2 w-full rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm"
            value={value.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            onBlur={handleBlur}
            disabled={disabled}
          />
          {errors.firstName && (
            <div className="text-2xs sm:text-xs text-red-600">
              {errors.firstName}
            </div>
          )}
          <div className="text-2xs sm:text-xs text-gray-500 mt-1">
            Enter name exactly as shown in passport
          </div>
        </div>
        <div className="sm:col-span-2">
          <input
            type="text"
            placeholder="Last Name"
            className="mt-1 block w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm"
            value={value.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            onBlur={handleBlur}
            disabled={disabled}
          />
          {errors.lastName && (
            <div className="text-2xs sm:text-xs text-red-600">
              {errors.lastName}
            </div>
          )}
          <div className="text-2xs sm:text-xs text-gray-500 mt-1">
            Enter name exactly as shown in passport
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-blue-600 mb-3">
          <h2 className="text-sm sm:text-subheading font-sora border-b pb-1 text-black mb-2 sm:mb-0">
            Contact Details{" "}
            <span className="hidden sm:inline mx-2 text-2xs sm:text-xs text-gray-500">
              (Your flight details will be sent here.)
            </span>
          </h2>
          <FaClipboardList className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-primary" />
        </div>
        <div className="text-2xs sm:hidden text-gray-500 mb-3">
          Your flight details will be sent here.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row">
            <select
              className="block p-2 rounded-t-md sm:rounded-l-md sm:rounded-tr-none border border-gray-300 shadow-sm text-xs sm:text-sm bg-white"
              value={selectedCountryCode}
              onChange={(e) => handleChange("countryCode", e.target.value)}
              style={{ minWidth: 90 }}
              disabled={disabled}
            >
              {countryDialInfo.map((c: any) => (
                <option key={`${c.dial_code}-${c.name}`} value={c.dial_code}>
                  {c.flag ? `${c.flag} ` : ""}
                  {c.name} ({c.dial_code})
                </option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Phone Number"
              className="block w-full p-2 rounded-b-md sm:rounded-r-md sm:rounded-bl-none border-t-0 sm:border-t border-b border-r sm:border-l-0 border-gray-300 shadow-sm text-xs sm:text-sm"
              value={value.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              onBlur={handleBlur}
              disabled={disabled}
            />
          </div>
          {errors.phone && (
            <div className="text-2xs sm:text-xs text-red-600 sm:col-span-2">
              {errors.phone}
            </div>
          )}
          <div>
            <input
              type="email"
              placeholder="Email"
              className="mt-1 block w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm"
              value={value.email}
              onChange={(e) => handleChange("email", e.target.value)}
              onBlur={handleBlur}
              disabled={disabled}
            />
            {errors.email && (
              <div className="text-2xs sm:text-xs text-red-600">
                {errors.email}
              </div>
            )}
          </div>
          {/* Airline + frequent flyer number. Hidden by default — see
              frequentFlyerEnabled. Both go together, because Sabre records the
              pair or neither. Nothing is removed: a number already on a
              passenger is still sent. */}
          {frequentFlyerEnabled && (
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Airline"
              maxLength={2}
              className="mt-1 block p-2 w-20 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm uppercase"
              value={value.ffAirline ?? ""}
              onChange={(e) => handleChange("ffAirline", e.target.value.toUpperCase().slice(0, 2))}
              disabled={disabled}
              aria-label="Frequent flyer airline"
            />
            <input
              type="text"
              placeholder="Frequent Flyer Number"
              className="mt-1 block p-2 w-full rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm"
              value={value.ffNumber}
              onChange={(e) => handleChange("ffNumber", e.target.value)}
              disabled={disabled}
              aria-label="Frequent flyer number"
            />
          </div>
          )}
        </div>
      </div>

      {/* ── Special requests ──────────────────────────────────────────────
          Sent to the airline as SSRs. Every one is a request the airline may
          decline, and none of them costs anything — chargeable services need
          an EMD and are deliberately not here. Infants get no meal or seat of
          their own, so the section is hidden for them. */}
      {type !== "INF" && (ssr.meal || ssr.assistance || ssr.seat_preference) && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-blue-600">
            <h2 className="text-sm sm:text-subheading font-sora border-b pb-1 text-black">
              Special Requests <span className="text-gray-400 font-normal">(optional)</span>
            </h2>
            <FaClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-3">
            <select
              className="block w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm bg-white"
              value={value.mealPreference ?? ""}
              onChange={(e) => handleChange("mealPreference", e.target.value)}
              disabled={disabled}
              aria-label="Meal preference"
            >
              <option value="">Meal preference</option>
              {(ssr.meal ?? []).map((m) => (
                <option key={m.code} value={m.code}>{m.label}</option>
              ))}
            </select>

            <select
              className="block w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm bg-white"
              value={value.assistance ?? ""}
              onChange={(e) => handleChange("assistance", e.target.value)}
              disabled={disabled}
              aria-label="Mobility assistance"
            >
              <option value="">Mobility assistance</option>
              {(ssr.assistance ?? []).map((a) => (
                <option key={a.code} value={a.code}>{a.label}</option>
              ))}
            </select>

            <select
              className="block w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm bg-white"
              value={value.seatPreference ?? ""}
              onChange={(e) => handleChange("seatPreference", e.target.value as any)}
              disabled={disabled}
              aria-label="Seat preference"
            >
              <option value="">Seat preference</option>
              <option value="window">Window seat preferred</option>
              <option value="aisle">Aisle seat preferred</option>
            </select>
          </div>

          {/* Said plainly, because an agent who promises a wheelchair and does
              not get one has a much worse conversation than one who explained. */}
          {/* The airline accepts a mobile, an email, or a declaration that the
              passenger refused both. Without one of the three it files a
              warning on the booking. Ticked deliberately, never inferred from a
              blank field — that would tell the carrier something untrue and
              hide a data-entry gap behind it. */}
          <label className="mt-3 flex items-start gap-2 text-2xs sm:text-xs text-gray-600">
            <input
              type="checkbox"
              checked={!!value.contactDeclined}
              onChange={(e) => handleChange("contactDeclined", e.target.checked as any)}
              disabled={disabled}
              className="mt-0.5"
            />
            <span>
              Passenger declined to give a contact number or email.
              {value.contactDeclined && (
                <span className="block text-gray-400">
                  The airline will be told they refused, and cannot contact them
                  about a delay or cancellation.
                </span>
              )}
            </span>
          </label>

          <p className="mt-2 text-2xs sm:text-xs text-gray-400">
            Requests are sent to the airline and confirmed by them, not by us.
          </p>
        </div>
      )}


      {type !== "INF" && (
        <div>
          <div className="flex items-center justify-between text-blue-600">
            <h2 className="text-sm sm:text-subheading font-sora border-b pb-1 text-black">
              Passport Details
            </h2>
            <FaClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          </div>
          <div className="grid grid-rows-2 gap-3 sm:gap-4 mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Passport Number"
                  className="mt-1 block w-full p-2 col-span-1 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm"
                  value={value.passportNumber}
                  onChange={(e) =>
                    handleChange("passportNumber", e.target.value)
                  }
                  onBlur={handleBlur}
                  disabled={disabled}
                />
                {errors.passportNumber && (
                  <div className="text-2xs sm:text-xs text-red-600">
                    {errors.passportNumber}
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <DatePickerWithValidation
                  selected={value.dob}
                  onSelect={(date) => handleChange("dob", date)}
                  passengerType={type}
                  showAgeValidation={true}
                  placeholder="Select date of birth"
                  disabled={disabled}
                />
                {errors.dob && (
                  <div className="text-2xs sm:text-xs text-red-600">
                    {errors.dob}
                  </div>
                )}
                <div className="text-2xs sm:text-xs text-gray-500 mt-1">
                  Enter date exactly as shown in passport
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="sm:col-span-2">
                <DatePickerWithValidation
                  selected={value.expiryDate}
                  onSelect={(date) => handleChange("expiryDate", date)}
                  minDate={new Date().toISOString().split("T")[0]}
                  placeholder="Select expiry date"
                  disabled={disabled}
                />
                {errors.expiryDate && (
                  <div className="text-2xs sm:text-xs text-red-600">
                    {errors.expiryDate}
                  </div>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Passport Country of Issue"
                  className="mt-1 block w-full p-2 rounded-md border border-gray-300 shadow-sm text-xs sm:text-sm"
                  value={value.issuingCountry}
                  onChange={(e) =>
                    handleChange("issuingCountry", e.target.value)
                  }
                  onBlur={handleBlur}
                  disabled={disabled}
                />
                {errors.issuingCountry && (
                  <div className="text-2xs sm:text-xs text-red-600">
                    {errors.issuingCountry}
                  </div>
                )}
              </div>
            </div>
            <div></div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6 justify-start items-start sm:items-center">
        <label className="flex items-start sm:items-center gap-2 text-xs sm:text-sm text-gray-700">
          <Checkbox
            checked={value.agreed}
            onChange={(e) => {
              handleChange("agreed", e.target.checked);
              validate("agreed", e.target.checked);
            }}
          />
          <span className="leading-tight sm:leading-normal">
            Agree To Terms & Conditions
          </span>
        </label>
        {errors.agreed && (
          <div className="text-2xs sm:text-xs text-red-600 w-full">
            {errors.agreed}
          </div>
        )}
      </div>
    </div>
  );
};

export default PassengerForm;
